import { makeAutoObservable } from 'mobx';
import { clearPersistedStore, makePersistable } from 'mobx-persist-store';

class ListStore {
  params = JSON.stringify({});

  // Memory-only: layout/search schemas and view version must not persist stale prefs.
  schemas = {};

  searchSchemas = {};

  viewConfigs = {};

  defaultStateValues = {
    paginationState: {
      pageIndex: 0,
      pageSize: 10,
    },
    filters: [],
    sortingState: [],
  };

  constructor(rootStore) {
    this.rootStore = rootStore;

    makeAutoObservable(this);

    makePersistable(this, {
      name: 'ListStore',
      properties: ['params'],
    });
  }

  getListValue(value) {
    return {
      paginationState: {
        ...this.defaultStateValues.paginationState,
        ...value?.paginationState,
      },
      sortingState: [
        ...this.defaultStateValues.sortingState,
        ...(value?.sortingState || []),
      ],
      filters: [...(value?.filters || [])],
    };
  }

  reset(key) {
    const current = JSON.parse(this.params);
    const newData = {
      ...current,
      [key]: {
        paginationState: this.defaultStateValues.paginationState,
        sortingState: this.defaultStateValues.sortingState,
        filters: [],
      },
    };
    this.params = JSON.stringify(newData);
  }

  set(key, value) {
    if (value) {
      const current = JSON.parse(this.params);
      const newValue = this.getListValue(value);

      const newData = {
        ...current,
        [key]: newValue,
      };
      this.params = JSON.stringify(newData);
    }
  }

  get(key, defaultStateValues = null) {
    const data = JSON.parse(this.params);
    if (!data?.[key]) {
      this.set(key, defaultStateValues || {});
    }
    return data?.[key] || this.defaultStateValues;
  }

  hasSchema(key) {
    return (
      Boolean(key) && Object.prototype.hasOwnProperty.call(this.schemas, key)
    );
  }

  setSchema(key, value) {
    if (!key || value === undefined) {
      return;
    }
    this.schemas = {
      ...this.schemas,
      [key]: value,
    };
  }

  getSchema(key) {
    if (!key) {
      return null;
    }
    return this.schemas[key] ?? null;
  }

  hasSearchSchema(key) {
    if (!key) {
      return false;
    }
    const value = this.searchSchemas[key];
    return Array.isArray(value) && value.length > 0;
  }

  setSearchSchema(key, value) {
    if (!key || value === undefined) {
      return;
    }
    this.searchSchemas = {
      ...this.searchSchemas,
      [key]: value,
    };
  }

  getSearchSchema(key) {
    if (!key) {
      return null;
    }
    return this.searchSchemas[key] ?? null;
  }

  setViewConfig(key, value) {
    if (!key || value === undefined) {
      return;
    }
    this.viewConfigs = {
      ...this.viewConfigs,
      [key]: value,
    };
  }

  getViewConfig(key) {
    if (!key) {
      return null;
    }
    return this.viewConfigs[key] ?? null;
  }

  async clearStore() {
    await clearPersistedStore(this);
  }
}

export default ListStore;
